import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ArrowLeft,
  ArrowRight,
  Scissors,
  User,
  Calendar as CalendarIcon,
  Clock,
  CreditCard,
  Check,
  Star,
  Wallet,
  Smartphone,
  Banknote,
  Store,
  Sparkles,
  Upload,
  Receipt,
  CheckCircle,
  Info,
  RefreshCw
} from "lucide-react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import UpsellModal from "@/components/UpsellModal";
import PayAtServiceReceiptUpload from "@/components/PayAtServiceReceiptUpload";

const STEPS = ["Service", "Stylist", "Date & Time", "Payment"];

const BookingPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  // Check for reschedule mode from location state
  const rescheduleData = location.state?.reschedule ? location.state : null;
  const isRescheduling = !!rescheduleData;
  
  // Check for embed mode and pre-selected stylist
  const isEmbedded = searchParams.get("embed") === "true";
  const preSelectedStylistId = searchParams.get("stylist");
  
  const [currentStep, setCurrentStep] = useState(preSelectedStylistId ? 0 : 0);
  const [services, setServices] = useState([]);
  const [stylists, setStylists] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Upsell Modal State
  const [showUpsellModal, setShowUpsellModal] = useState(false);
  const [pendingService, setPendingService] = useState(null);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [bundleTotal, setBundleTotal] = useState(null);
  
  // Receipt Upload State (for manual payments)
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [confirmedAppointment, setConfirmedAppointment] = useState(null);
  const [stylistPaymentInfo, setStylistPaymentInfo] = useState(null);
  
  const [booking, setBooking] = useState({
    service: null,
    stylist: null,
    date: null,
    time: null,
    paymentMethod: null,
    notes: "",
    addons: []
  });

  useEffect(() => {
    fetchInitialData();
  }, []);

  // Pre-select stylist if provided in URL
  useEffect(() => {
    if (preSelectedStylistId && stylists.length > 0) {
      const stylist = stylists.find(s => s.user_id === preSelectedStylistId);
      if (stylist) {
        setBooking(prev => ({ ...prev, stylist }));
        // If stylist pre-selected, skip to service selection
        if (currentStep === 1) setCurrentStep(0);
      }
    }
  }, [preSelectedStylistId, stylists]);

  useEffect(() => {
    if (booking.stylist && booking.date) {
      fetchAvailability();
    }
  }, [booking.stylist, booking.date]);

  // Pre-fill data when rescheduling
  useEffect(() => {
    if (isRescheduling && rescheduleData?.existingData && services.length > 0 && stylists.length > 0) {
      const existingData = rescheduleData.existingData;
      
      // Find the stylist and service from loaded data
      const stylist = stylists.find(s => s.user_id === existingData.stylist_id);
      const service = services.find(s => s.service_id === existingData.service_id);
      
      if (stylist && service) {
        setBooking(prev => ({
          ...prev,
          stylist,
          service,
          // Don't pre-fill date/time - let user pick new ones
        }));
        
        // Skip to date/time selection step
        setCurrentStep(2);
        toast.info(`Rescheduling: ${service.name} with ${stylist.user?.name || existingData.stylist_name}`);
      }
    }
  }, [isRescheduling, rescheduleData, services, stylists]);

  const fetchInitialData = async () => {
    try {
      const [servicesRes, stylistsRes] = await Promise.all([
        axios.get(`${API}/services/`),
        axios.get(`${API}/stylists/`)
      ]);
      setServices(servicesRes.data);
      setStylists(stylistsRes.data);
      
      // Set default payment methods - these will be updated when a stylist is selected
      const defaultMethods = [
        {
          id: "demo",
          name: "🎮 Demo Mode",
          description: "Test the complete booking flow - No real payment",
          instructions: "This is a demo payment. Click to instantly confirm your booking without any real charges.",
          isDemo: true
        },
        {
          id: "stripe",
          name: "Credit/Debit Card",
          description: "Pay securely with your card",
          instructions: null
        },
        {
          id: "cash",
          name: "Pay at Appointment",
          description: "Cash or card in person",
          instructions: "You can pay when you arrive for your appointment"
        }
      ];
      
      setPaymentMethods(defaultMethods);
    } catch (error) {
      toast.error("Failed to load booking data");
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailability = async () => {
    if (!booking.stylist || !booking.date) return;
    
    try {
      const dateStr = format(booking.date, "yyyy-MM-dd");
      const response = await axios.get(
        `${API}/stylists/${booking.stylist.user_id}/availability?date=${dateStr}`
      );
      setAvailableSlots(response.data.slots);
    } catch (error) {
      toast.error("Failed to load availability");
    }
  };

  // Fetch payment methods for selected stylist
  const fetchStylistPaymentMethods = async (stylistId) => {
    try {
      const response = await axios.get(`${API}/payments/stylist/${stylistId}/methods`);
      const methodsData = response.data.methods || {};
      const availableMethods = [];
      
      // Demo Mode - Always first (for testing)
      availableMethods.push({
        id: "demo",
        name: "🎮 Demo Mode",
        description: "Test the complete booking flow - No real payment",
        instructions: "This is a demo payment. Click to instantly confirm your booking without any real charges.",
        isDemo: true
      });
      
      // Card payments (Stripe)
      if (methodsData.stripe_enabled !== false) {
        availableMethods.push({
          id: "stripe",
          name: "Credit/Debit Card",
          description: "Pay securely with your card",
          instructions: null
        });
      }
      
      // Cash App
      if (methodsData.cashapp_available && methodsData.cashapp_tag) {
        availableMethods.push({
          id: "cashapp",
          name: "Cash App",
          description: `Send payment to ${methodsData.cashapp_tag}`,
          instructions: `Open Cash App and send payment to ${methodsData.cashapp_tag}`,
          tag: methodsData.cashapp_tag
        });
      }
      
      // Zelle
      if (methodsData.zelle_available) {
        availableMethods.push({
          id: "zelle",
          name: "Zelle",
          description: `Send payment via Zelle`,
          instructions: methodsData.zelle_email 
            ? `Send Zelle payment to: ${methodsData.zelle_email}`
            : `Send Zelle payment to: ${methodsData.zelle_phone}`,
          email: methodsData.zelle_email,
          phone: methodsData.zelle_phone
        });
      }
      
      // PayPal
      if (methodsData.paypal_available && methodsData.paypal_link) {
        availableMethods.push({
          id: "paypal",
          name: "PayPal",
          description: "Pay with your PayPal account",
          instructions: `Pay via PayPal`,
          link: methodsData.paypal_link
        });
      }
      
      // Cash at appointment (always available)
      availableMethods.push({
        id: "cash",
        name: "Pay at Appointment",
        description: "Cash or card in person",
        instructions: "You can pay when you arrive for your appointment"
      });
      
      setPaymentMethods(availableMethods);
    } catch (error) {
      console.error("Failed to fetch stylist payment methods:", error);
      // Fallback to default methods with Demo Mode
      setPaymentMethods([
        { id: "demo", name: "🎮 Demo Mode", description: "Test the complete booking flow - No real payment", isDemo: true },
        { id: "stripe", name: "Credit/Debit Card", description: "Pay securely with your card" },
        { id: "cash", name: "Pay at Appointment", description: "Cash or card in person" }
      ]);
    }
  };

  // When stylist is selected, fetch their payment methods
  useEffect(() => {
    if (booking.stylist?.user_id) {
      fetchStylistPaymentMethods(booking.stylist.user_id);
    }
  }, [booking.stylist]);

  const handleNext = () => {
    // For rescheduling, go directly to confirmation after date/time selection
    if (isRescheduling && currentStep === 2) {
      // Submit the reschedule directly
      handleSubmit();
      return;
    }
    
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    // For rescheduling, go back to dashboard instead of previous steps
    if (isRescheduling && currentStep <= 2) {
      navigate("/dashboard");
      return;
    }
    
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const canProceed = () => {
    // For rescheduling, only need date and time since service/stylist are pre-selected
    if (isRescheduling) {
      return booking.date !== null && booking.time !== null;
    }
    
    switch (currentStep) {
      case 0: return booking.service !== null;
      case 1: return booking.stylist !== null;
      case 2: return booking.date !== null && booking.time !== null;
      case 3: return booking.paymentMethod !== null;
      default: return false;
    }
  };

  // Handle service selection - show upsell modal if stylist is Pro/Elite or user is super admin
  const handleServiceSelect = (service) => {
    // If stylist is already selected and is Pro/Elite (or user is super admin), show upsell modal
    const stylistIsPremium = booking.stylist?.membership?.tier && booking.stylist.membership.tier !== "free";
    const userIsSuperAdmin = user?.is_super_admin;
    
    if (stylistIsPremium || userIsSuperAdmin) {
      setPendingService(service);
      setShowUpsellModal(true);
    } else {
      // Just select the service directly for free-tier stylists
      setBooking({ ...booking, service, addons: [] });
      setSelectedAddons([]);
      setBundleTotal(null);
    }
  };

  // Handle upsell modal confirmation
  const handleUpsellConfirm = (allServices, bundlePricing) => {
    if (allServices.length > 0) {
      const mainService = allServices[0];
      const addons = allServices.slice(1);
      setBooking({ 
        ...booking, 
        service: mainService, 
        addons: addons
      });
      setSelectedAddons(addons);
      setBundleTotal(bundlePricing);
    }
    setShowUpsellModal(false);
    setPendingService(null);
  };

  // Handle service click - show upsell modal for add-on suggestions
  const onServiceClick = (service) => {
    // Show upsell modal to suggest add-ons for the selected service
    // This helps increase average order value
    if (service.suggested_with && service.suggested_with.length > 0) {
      setPendingService(service);
      setShowUpsellModal(true);
    } else {
      // No suggested add-ons, just select the service
      setBooking({ ...booking, service, addons: [] });
      setSelectedAddons([]);
      setBundleTotal(null);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      // If rescheduling, update the existing appointment
      if (isRescheduling && rescheduleData?.appointmentId) {
        await axios.put(
          `${API}/appointments/${rescheduleData.appointmentId}/reschedule`,
          {
            date: format(booking.date, "yyyy-MM-dd"),
            time: booking.time,
            service_id: booking.service.service_id,
            notes: booking.notes
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        toast.success("Appointment rescheduled successfully!");
        navigate("/dashboard");
        return;
      }
      
      // Otherwise, create a new appointment
      // Determine if this is a "pay at service" method
      const isPayAtService = ["paypal", "cashapp", "zelle", "cash"].includes(booking.paymentMethod.id);
      
      const appointmentData = {
        service_id: booking.service.service_id,
        stylist_id: booking.stylist.user_id,
        date: format(booking.date, "yyyy-MM-dd"),
        time: booking.time,
        payment_method: booking.paymentMethod.id,
        notes: booking.notes,
        // Mark as pending payment for pay-at-service methods
        payment_status: isPayAtService ? "pending" : "processing"
      };

      const response = await axios.post(`${API}/appointments/`, appointmentData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Handle Demo Mode payment - instant success
      if (booking.paymentMethod.id === "demo") {
        await axios.post(
          `${API}/payments/demo/checkout?appointment_id=${response.data.appointment_id}`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("🎉 Demo booking confirmed! Payment simulated successfully.");
        navigate("/dashboard");
      }
      // If Stripe/Apple Pay/Google Pay payment, redirect to checkout (prepay)
      else if (["stripe", "apple_pay", "google_pay"].includes(booking.paymentMethod.id)) {
        const checkoutRes = await axios.post(
          `${API}/payments/checkout/session?appointment_id=${response.data.appointment_id}`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        window.location.href = checkoutRes.data.checkout_url;
      }
      // Pay at service methods (PayPal, CashApp, Zelle) - NO prepayment, pay at appointment
      else if (["paypal", "cashapp", "zelle"].includes(booking.paymentMethod.id)) {
        // Fetch stylist's payment info
        try {
          const paymentInfoRes = await axios.get(
            `${API}/stylists/${booking.stylist.user_id}/payment-info`
          );
          setStylistPaymentInfo(paymentInfoRes.data);
        } catch (e) {
          console.log("Could not fetch stylist payment info");
        }
        
        setConfirmedAppointment({
          appointment_id: response.data.appointment_id,
          amount: booking.service.price + (selectedAddons?.reduce((sum, a) => sum + (a.price || 0), 0) || 0),
          payment_method: booking.paymentMethod.id,
          appointment_date: booking.date,
          appointment_time: booking.time,
          service_name: booking.service.name
        });
        setShowReceiptModal(true);
      }
      // Cash payment - just confirm, pay at salon
      else {
        toast.success("Appointment booked! Pay cash at the salon.");
        navigate("/dashboard");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Booking failed");
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleReceiptSuccess = () => {
    toast.success("Receipt uploaded! Your stylist will verify and confirm your appointment.");
    setShowReceiptModal(false);
    navigate("/dashboard");
  };
  
  const handleSkipReceipt = () => {
    toast.info("You can upload your payment receipt later from your dashboard.");
    setShowReceiptModal(false);
    navigate("/dashboard");
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case "haircut": return "✂️";
      case "color": return "🎨";
      case "styling": return "✨";
      case "treatment": return "💆";
      case "beard": return "🧔";
      case "spa": return "🧖";
      default: return "💇";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-emerald-950 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-stone-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <header className="sticky top-0 z-30 glass border-b border-stone-200">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate("/dashboard")}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex-1">
              <h1 className="font-display text-xl font-bold text-emerald-950 flex items-center gap-2">
                {isRescheduling ? (
                  <>
                    <RefreshCw className="w-5 h-5 text-amber-600" />
                    Reschedule Appointment
                  </>
                ) : (
                  "Book an Appointment"
                )}
              </h1>
              <p className="text-sm text-stone-600">
                {isRescheduling ? (
                  <>Changing: {rescheduleData?.existingData?.service_name} with {rescheduleData?.existingData?.stylist_name}</>
                ) : (
                  <>Step {currentStep + 1} of {STEPS.length}: {STEPS[currentStep]}</>
                )}
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Reschedule Banner */}
      {isRescheduling && (
        <div className="bg-amber-50 border-b border-amber-200 px-6 py-3">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-2 text-amber-800">
              <RefreshCw className="w-4 h-4" />
              <span className="text-sm">
                <strong>Rescheduling:</strong> Pick a new date and time below. Your stylist and service are pre-selected.
              </span>
            </div>
            
            {/* Payment Status Banner */}
            {rescheduleData?.existingData?.payment_status === 'paid' || rescheduleData?.existingData?.receipt_status === 'verified' ? (
              <div className="mt-2 flex items-center gap-2 text-emerald-700 bg-emerald-50 rounded-lg p-2">
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm">
                  <strong>Payment Verified</strong> - Your payment will carry over to the new appointment.
                </span>
              </div>
            ) : rescheduleData?.existingData?.receipt_status === 'pending_verification' ? (
              <div className="mt-2 flex items-center gap-2 text-blue-700 bg-blue-50 rounded-lg p-2">
                <Clock className="w-4 h-4" />
                <span className="text-sm">
                  <strong>Receipt Pending</strong> - Your uploaded receipt will carry over to the new appointment.
                </span>
              </div>
            ) : ['zelle', 'cashapp', 'paypal'].includes(rescheduleData?.existingData?.payment_method) ? (
              <div className="mt-2 flex items-center gap-2 text-amber-700 bg-amber-100 rounded-lg p-2">
                <Upload className="w-4 h-4" />
                <span className="text-sm">
                  <strong>Payment Reminder:</strong> Don&apos;t forget to upload your {rescheduleData?.existingData?.payment_method} screenshot after rescheduling!
                </span>
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* Progress Bar */}
      <div className="max-w-4xl mx-auto px-6 py-6">
        <div className="flex items-center gap-2">
          {(isRescheduling ? ["Date & Time", "Confirm"] : STEPS).map((step, index) => (
            <div key={step} className="flex-1 flex items-center">
              <div 
                className={`w-full h-2 rounded-full ${
                  index <= (isRescheduling ? currentStep - 2 : currentStep) ? 'bg-emerald-600' : 'bg-stone-200'
                }`}
              ></div>
            </div>
          ))}
        </div>
        <div className="flex justify-between mt-2">
          {(isRescheduling ? ["Date & Time", "Confirm"] : STEPS).map((step, index) => (
            <span 
              key={step}
              className={`text-xs ${index <= (isRescheduling ? currentStep - 2 : currentStep) ? 'text-emerald-600' : 'text-stone-400'}`}
            >
              {step}
            </span>
          ))}
        </div>
      </div>

      {/* Content - extra padding at bottom for fixed footer */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 pb-36 sm:pb-28">
        {/* Step 1: Select Service */}
        {currentStep === 0 && (
          <div className="space-y-4 animate-fade-in" data-testid="step-service">
            <h2 className="font-display text-2xl font-bold text-emerald-950 mb-6">
              Choose Your Service
            </h2>
            
            {/* Show selected add-ons summary if any */}
            {selectedAddons.length > 0 && bundleTotal && (
              <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-xl p-4 mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-5 h-5 text-amber-600" />
                  <span className="font-semibold text-amber-900">Enhanced Service Bundle</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Badge className="bg-amber-100 text-amber-800 border-amber-300">
                    {booking.service?.name}
                  </Badge>
                  {selectedAddons.map((addon) => (
                    <Badge key={addon.service_id} variant="outline" className="border-amber-300 text-amber-700">
                      + {addon.name}
                    </Badge>
                  ))}
                </div>
                <div className="mt-2 text-sm text-amber-700">
                  Total: <span className="font-bold">${bundleTotal.total_price?.toFixed(2)}</span>
                  {bundleTotal.savings > 0 && (
                    <span className="ml-2 text-emerald-600">(Save ${bundleTotal.savings?.toFixed(2)}!)</span>
                  )}
                </div>
              </div>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {services.map((service) => (
                <div
                  key={service.service_id}
                  className={`service-card ${booking.service?.service_id === service.service_id ? 'service-card-selected' : ''}`}
                  onClick={() => onServiceClick(service)}
                  data-testid={`service-${service.service_id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <span className="text-2xl">{getCategoryIcon(service.category)}</span>
                    <span className="font-bold text-emerald-950">${(service.base_price || 0).toFixed(2)}</span>
                  </div>
                  <h3 className="font-medium text-lg text-emerald-950 mb-1">{service.name}</h3>
                  <p className="text-sm text-stone-600 mb-3">{service.description}</p>
                  <div className="flex items-center gap-2 text-sm text-stone-500">
                    <Clock className="w-4 h-4" />
                    <span>{service.duration_minutes} min</span>
                  </div>
                  {booking.service?.service_id === service.service_id && (
                    <div className="absolute top-4 right-4">
                      <div className="w-6 h-6 bg-emerald-600 rounded-full flex items-center justify-center">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Select Stylist */}
        {currentStep === 1 && (
          <div className="space-y-4 animate-fade-in" data-testid="step-stylist">
            <h2 className="font-display text-2xl font-bold text-emerald-950 mb-6">
              Choose Your Stylist
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {stylists.map((stylist) => (
                <div
                  key={stylist.user_id}
                  className={`stylist-card ${booking.stylist?.user_id === stylist.user_id ? 'stylist-card-selected' : ''}`}
                  onClick={() => setBooking({ ...booking, stylist })}
                  data-testid={`stylist-${stylist.user_id}`}
                >
                  <div className="h-48 bg-gradient-to-br from-emerald-100 to-stone-100 flex items-center justify-center">
                    <div className="w-24 h-24 bg-emerald-950 rounded-full flex items-center justify-center">
                      <User className="w-12 h-12 text-white" />
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="font-display font-semibold text-lg text-emerald-950 mb-1">
                      {stylist.user?.name}
                    </h3>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                        <span className="font-medium text-stone-700">{(stylist.rating || 0).toFixed(1)}</span>
                      </div>
                      <span className="text-stone-400">•</span>
                      <span className="text-sm text-stone-600">{stylist.total_reviews || 0} reviews</span>
                    </div>
                    <p className="text-sm text-stone-600 mb-3 line-clamp-2">{stylist.bio}</p>
                    <div className="flex flex-wrap gap-2">
                      {stylist.specialties?.slice(0, 3).map((specialty, i) => (
                        <Badge key={i} variant="secondary" className="text-xs">
                          {specialty}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  {booking.stylist?.user_id === stylist.user_id && (
                    <div className="absolute top-4 right-4">
                      <div className="w-6 h-6 bg-emerald-600 rounded-full flex items-center justify-center">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step 3: Select Date & Time */}
        {currentStep === 2 && (
          <div className="space-y-6 animate-fade-in" data-testid="step-datetime">
            <h2 className="font-display text-2xl font-bold text-emerald-950 mb-6">
              Choose Date & Time
            </h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <Card className="card-dashboard">
                <CardContent className="pt-6">
                  <Label className="mb-4 block">Select Date</Label>
                  <Calendar
                    mode="single"
                    selected={booking.date}
                    onSelect={(date) => setBooking({ ...booking, date, time: null })}
                    disabled={(date) => date < new Date() || date.getDay() === 0}
                    className="rounded-md border mx-auto"
                    data-testid="booking-calendar"
                  />
                </CardContent>
              </Card>

              <Card className="card-dashboard">
                <CardContent className="pt-6">
                  <Label className="mb-4 block">Select Time</Label>
                  {!booking.date ? (
                    <p className="text-stone-500 text-center py-8">
                      Please select a date first
                    </p>
                  ) : availableSlots.length === 0 ? (
                    <p className="text-stone-500 text-center py-8">
                      Loading availability...
                    </p>
                  ) : (
                    <div className="grid grid-cols-3 gap-3" data-testid="time-slots">
                      {availableSlots.map((slot) => (
                        <button
                          key={slot.time}
                          className={`time-slot ${
                            booking.time === slot.time ? 'time-slot-selected' : ''
                          } ${!slot.available ? 'time-slot-unavailable' : ''}`}
                          onClick={() => slot.available && setBooking({ ...booking, time: slot.time })}
                          disabled={!slot.available}
                          data-testid={`time-${slot.time}`}
                        >
                          {slot.time}
                        </button>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            <div>
              <Label htmlFor="notes">Special Requests (optional)</Label>
              <Textarea
                id="notes"
                placeholder="Any special requests or notes for your stylist..."
                value={booking.notes}
                onChange={(e) => setBooking({ ...booking, notes: e.target.value })}
                className="mt-2 bg-white"
                data-testid="notes-input"
              />
            </div>
          </div>
        )}

        {/* Step 4: Payment */}
        {currentStep === 3 && (
          <div className="space-y-6 animate-fade-in" data-testid="step-payment">
            <h2 className="font-display text-2xl font-bold text-emerald-950 mb-6">
              Choose Payment Method
            </h2>

            {/* Order Summary */}
            <Card className="card-dashboard bg-emerald-50 border-emerald-100">
              <CardContent className="pt-6">
                <h3 className="font-medium text-emerald-950 mb-4">Appointment Summary</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-stone-600">Service</span>
                    <span className="font-medium text-emerald-950">{booking.service?.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-stone-600">Stylist</span>
                    <span className="font-medium text-emerald-950">{booking.stylist?.user?.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-stone-600">Date & Time</span>
                    <span className="font-medium text-emerald-950">
                      {booking.date && format(booking.date, "MMM d, yyyy")} at {booking.time}
                    </span>
                  </div>
                  <hr className="border-emerald-200" />
                  <div className="flex justify-between text-lg">
                    <span className="font-medium text-emerald-950">Total</span>
                    <span className="font-bold text-emerald-950">${(booking.service?.base_price || 0).toFixed(2)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Payment Methods */}
            <div className="space-y-4">
              {paymentMethods.map((method) => {
                const IconComponent = 
                  method.id === "stripe" ? CreditCard :
                  method.id === "paypal" ? Wallet :
                  method.id === "cashapp" ? Smartphone :
                  method.id === "zelle" ? Banknote : Store;

                return (
                  <div
                    key={method.id}
                    className={`payment-method ${booking.paymentMethod?.id === method.id ? 'payment-method-selected' : ''}`}
                    onClick={() => setBooking({ ...booking, paymentMethod: method })}
                    data-testid={`payment-${method.id}`}
                  >
                    <div className="w-12 h-12 bg-stone-100 rounded-lg flex items-center justify-center">
                      <IconComponent className="w-6 h-6 text-emerald-950" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-emerald-950">{method.name}</h4>
                      <p className="text-sm text-stone-600">{method.description}</p>
                      
                      {/* Show payment details when selected */}
                      {booking.paymentMethod?.id === method.id && method.instructions && (
                        <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                          <p className="text-sm text-amber-800 font-medium mb-1">Payment Instructions:</p>
                          <p className="text-sm text-amber-700">{method.instructions}</p>
                          
                          {/* Cash App - Show clickable tag */}
                          {method.id === "cashapp" && method.tag && (
                            <a 
                              href={`https://cash.app/${method.tag.replace('$', '')}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 mt-2 px-3 py-1.5 bg-[#00D632] text-white text-sm font-medium rounded-lg hover:bg-[#00B82B]"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Smartphone className="w-4 h-4" />
                              Open Cash App
                            </a>
                          )}
                          
                          {/* PayPal - Show clickable link */}
                          {method.id === "paypal" && method.link && (
                            <a 
                              href={method.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 mt-2 px-3 py-1.5 bg-[#0070BA] text-white text-sm font-medium rounded-lg hover:bg-[#005C96]"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Wallet className="w-4 h-4" />
                              Pay with PayPal
                            </a>
                          )}
                          
                          {/* Zelle - Show copy-able info */}
                          {method.id === "zelle" && (
                            <div className="mt-2 flex flex-wrap gap-2">
                              {method.email && (
                                <button
                                  className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#6D1ED4] text-white text-sm font-medium rounded-lg hover:bg-[#5A18B0]"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigator.clipboard.writeText(method.email);
                                    toast.success("Email copied to clipboard!");
                                  }}
                                >
                                  <Banknote className="w-4 h-4" />
                                  Copy Email: {method.email}
                                </button>
                              )}
                              {method.phone && (
                                <button
                                  className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#6D1ED4] text-white text-sm font-medium rounded-lg hover:bg-[#5A18B0]"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigator.clipboard.writeText(method.phone);
                                    toast.success("Phone copied to clipboard!");
                                  }}
                                >
                                  <Banknote className="w-4 h-4" />
                                  Copy Phone: {method.phone}
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    {booking.paymentMethod?.id === method.id && (
                      <div className="w-6 h-6 bg-emerald-600 rounded-full flex items-center justify-center">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Footer Navigation - positioned above Emergent badge on mobile */}
      <div className="fixed bottom-12 sm:bottom-0 left-0 right-0 bg-white border-t border-stone-200 p-3 sm:p-4 z-50 shadow-lg">
        <div className="max-w-4xl mx-auto flex justify-between items-center gap-3">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={isRescheduling ? false : currentStep === 0}
            className="flex-1 h-12"
            data-testid="prev-step-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {isRescheduling ? "Cancel" : "Back"}
          </Button>

          {/* For rescheduling on step 2 (Date & Time), show Confirm Reschedule button */}
          {isRescheduling && currentStep === 2 ? (
            <Button
              onClick={handleSubmit}
              disabled={!canProceed() || submitting}
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-sm flex-1 h-12 text-base font-semibold"
              data-testid="confirm-reschedule-btn"
            >
              {submitting ? "Processing..." : "✓ Confirm Reschedule"}
            </Button>
          ) : currentStep < STEPS.length - 1 ? (
            <Button
              onClick={handleNext}
              disabled={!canProceed()}
              className="btn-primary rounded-sm flex-1 h-12 text-base font-semibold"
              data-testid="next-step-btn"
            >
              Continue
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={!canProceed() || submitting}
              className={`rounded-sm flex-1 h-12 ${
                booking.paymentMethod?.id === "demo" 
                  ? "bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white" 
                  : "btn-primary"
              }`}
              data-testid="confirm-booking-btn"
            >
              {submitting ? "Processing..." : 
                booking.paymentMethod?.id === "demo" ? "🎮 Complete Demo" :
                booking.paymentMethod?.id === "stripe" ? "Proceed to Pay" : "Confirm Booking"
              }
            </Button>
          )}
        </div>
      </div>

      {/* Upsell Modal - Shows add-on suggestions when service is selected */}
      <UpsellModal
        isOpen={showUpsellModal}
        onClose={() => {
          setShowUpsellModal(false);
          // If closed without selecting, still select the service
          if (pendingService) {
            setBooking({ ...booking, service: pendingService, addons: [] });
            setPendingService(null);
          }
        }}
        mainService={pendingService}
        stylistId={booking.stylist?.user_id}
        onAddServices={handleUpsellConfirm}
      />

      {/* Pay at Service Receipt Upload Modal */}
      {confirmedAppointment && (
        <PayAtServiceReceiptUpload
          isOpen={showReceiptModal}
          onClose={() => {
            setShowReceiptModal(false);
            toast.info("Your appointment is confirmed. Remember to pay at your appointment and upload your receipt!");
            navigate("/dashboard");
          }}
          appointmentId={confirmedAppointment.appointment_id}
          paymentMethod={confirmedAppointment.payment_method}
          amount={confirmedAppointment.amount}
          stylistPaymentInfo={stylistPaymentInfo}
          onSuccess={handleReceiptSuccess}
        />
      )}
    </div>
  );
};

export default BookingPage;
